/**
 * ============================================================================
 * KbSpaceCollectionsTree — Árvore de Coleções + Páginas do space
 * ----------------------------------------------------------------------------
 * Renderiza a estrutura completa:
 *   Space
 *     ├─ Coleção (recursiva) → subcoleções → páginas
 *     └─ Sem coleção → páginas órfãs
 *
 * Recursos:
 *   - Criar/editar/excluir coleções via dialogs
 *   - Criar página dentro de qualquer coleção (ou raiz)
 *   - Drag-and-drop:
 *       • Reordenar coleções irmãs (mesmo parent)
 *       • Arrastar página entre coleções (usa kb_move_page)
 *   - Estado de expansão persistido em localStorage por space
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Fase C: item "Gerenciar acesso" (ACL) por coleção e botão de ACL do space.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown, ChevronRight, FileText, Folder, MoreHorizontal,
  Plus, Pencil, Trash2, FolderPlus, FilePlus, ShieldCheck,
} from "lucide-react";
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor,
  useSensor, useSensors, useDroppable, useDraggable,
  MeasuringStrategy, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  useKbCollections, useKbPagesTree, useReorderKbCollections, useMoveKbPage,
  useCreateKbPage, type KbCollectionRow,
} from "@/hooks/kb/useKb";
import { KbCollectionDialog } from "./KbCollectionDialog";
import { KbDeleteCollectionDialog } from "./KbDeleteCollectionDialog";
import { KbAclDialog } from "./KbAclDialog";

// ---------------------------------------------------------------------------
// Tipos internos
// ---------------------------------------------------------------------------
interface PageRow {
  id: string;
  parent_id: string | null;
  collection_id: string | null;
  title: string;
  status: string;
  page_position: number | null;
  updated_at: string;
}

const toSlug = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

// ---------------------------------------------------------------------------
// Item: Página (draggable)
// ---------------------------------------------------------------------------
function PageItem({ page, spaceId, depth, activePageId }: {
  page: PageRow; spaceId: string; depth: number; activePageId?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `page:${page.id}`,
    data: { type: "page", pageId: page.id, collectionId: page.collection_id },
  });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };
  const active = page.id === activePageId;
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, paddingLeft: 8 + depth * 14 }}
      className={cn(
        "group flex items-center gap-1.5 rounded px-1.5 py-1 text-sm hover:bg-accent",
        active && "bg-accent font-medium",
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 opacity-0 group-hover:opacity-60"
        aria-label="Arrastar página"
      >
        <MoreHorizontal className="h-3 w-3" />
      </button>
      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <Link to={`/tenant/kb/${spaceId}/pages/${page.id}`} className="truncate flex-1">
        {page.title || "(sem título)"}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item: Coleção (sortable + droppable p/ receber páginas e subcoleções)
// ---------------------------------------------------------------------------
function CollectionItem({
  collection, spaceId, depth, expanded, onToggle,
  children, allCollections, activePageId, onCreatePage,
}: {
  collection: KbCollectionRow;
  spaceId: string;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  allCollections: KbCollectionRow[];
  activePageId?: string;
  onCreatePage: (collectionId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `col:${collection.id}`,
    data: { type: "collection", collectionId: collection.id, parentId: collection.parent_id },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `col-drop:${collection.id}`,
    data: { type: "collection-drop", collectionId: collection.id },
  });

  const [editOpen, setEditOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [aclOpen, setAclOpen] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        ref={setDropRef}
        style={{ paddingLeft: 4 + depth * 14 }}
        className={cn(
          "group flex items-center gap-1 rounded px-1 py-1 text-sm hover:bg-accent",
          isOver && "ring-2 ring-primary/60 bg-primary/5",
        )}
      >
        <button
          onClick={onToggle}
          className="p-0.5 hover:bg-muted rounded"
          aria-label={expanded ? "Recolher" : "Expandir"}
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-base leading-none select-none"
          aria-label="Arrastar coleção"
        >
          {collection.icon || (expanded ? "📂" : "📁")}
        </span>
        <span className="flex-1 truncate font-medium">{collection.name}</span>
        {collection.page_count > 0 && (
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
            {collection.page_count}
          </Badge>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-muted rounded"
              aria-label="Ações da coleção"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => onCreatePage(collection.id)}>
              <FilePlus className="h-4 w-4 mr-2" /> Nova página aqui
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSubOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" /> Nova subcoleção
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4 mr-2" /> Renomear / editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAclOpen(true)}>
              <ShieldCheck className="h-4 w-4 mr-2" /> Gerenciar acesso
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDelOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {expanded && <div>{children}</div>}

      <KbCollectionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        spaceId={spaceId}
        collection={collection}
      />
      <KbCollectionDialog
        open={subOpen}
        onOpenChange={setSubOpen}
        spaceId={spaceId}
        parentId={collection.id}
      />
      <KbDeleteCollectionDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        spaceId={spaceId}
        collection={collection}
        allCollections={allCollections}
      />
      <KbAclDialog
        open={aclOpen}
        onOpenChange={setAclOpen}
        scope="collection"
        scopeId={collection.id}
        title={collection.name}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zona droppable "raiz / sem coleção" (para páginas)
// ---------------------------------------------------------------------------
function OrphanDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-drop:__orphan__`,
    data: { type: "collection-drop", collectionId: null },
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-md transition-colors",
        isOver && "ring-2 ring-primary/60 bg-primary/5",
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
interface Props {
  spaceId: string;
  activePageId?: string;
}

export function KbSpaceCollectionsTree({ spaceId, activePageId }: Props) {
  const navigate = useNavigate();
  const collectionsQuery = useKbCollections(spaceId);
  const pagesQuery = useKbPagesTree(spaceId);
  const reorderMut = useReorderKbCollections(spaceId);
  const moveMut = useMoveKbPage(spaceId);
  const createPage = useCreateKbPage();

  const collections = collectionsQuery.data ?? [];
  const pages = (pagesQuery.data ?? []) as PageRow[];

  const [rootCreateOpen, setRootCreateOpen] = useState(false);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [pendingCollectionId, setPendingCollectionId] = useState<string | null>(null);
  const [newPageTitle, setNewPageTitle] = useState("");

  // Estado de expansão persistido por space
  const storageKey = `kb-tree-expanded:${spaceId}`;
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(expanded))); } catch {}
  }, [storageKey, expanded]);

  // Auto-expande coleções raízes na primeira renderização com dados
  useEffect(() => {
    if (collections.length > 0 && expanded.size === 0) {
      const roots = collections.filter((c) => !c.parent_id).map((c) => c.id);
      setExpanded(new Set(roots));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections.length]);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Indexa coleções por parent para render recursivo
  const collectionsByParent = useMemo(() => {
    const m = new Map<string | null, KbCollectionRow[]>();
    collections.forEach((c) => {
      const key = c.parent_id ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    });
    m.forEach((arr) => arr.sort((a, b) => a.col_position - b.col_position));
    return m;
  }, [collections]);

  const pagesByCollection = useMemo(() => {
    const m = new Map<string | null, PageRow[]>();
    pages.forEach((p) => {
      const key = p.collection_id ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    });
    return m;
  }, [pages]);

  // ---- DnD handlers ------------------------------------------------------
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const aData: any = active.data.current;
    const oData: any = over.data.current;
    if (!aData) return;

    // Página → soltar em coleção
    // Aceita tanto o droppable interno ("collection-drop") quanto o próprio
    // sortable externo ("collection"), pois a resolução de colisão pode
    // escolher qualquer um dos dois nós sobrepostos.
    if (aData.type === "page" && (oData?.type === "collection-drop" || oData?.type === "collection")) {
      const targetCollectionId: string | null = oData.collectionId ?? null;
      if (aData.collectionId === targetCollectionId) return;
      try {
        await moveMut.mutateAsync({
          page_id: aData.pageId,
          collection_id: targetCollectionId,
          clear_collection: targetCollectionId == null,
          clear_parent: true, // ao mover entre coleções, remove parent de página
        });
      } catch (err: any) {
        toast({ title: "Falha ao mover página", description: err?.message, variant: "destructive" });
      }
      return;
    }


    // Coleção → reordenar entre irmãs (sortable)
    if (aData.type === "collection" && String(active.id) !== String(over.id)) {
      const activeCollectionId: string = aData.collectionId;
      const parentId: string | null = aData.parentId ?? null;
      const siblings = (collectionsByParent.get(parentId) ?? []).map((c) => c.id);
      const oldIndex = siblings.indexOf(activeCollectionId);
      // over pode ser outra coleção sortable ou um droppable — tenta extrair o id
      let overCollectionId: string | null = null;
      if (oData?.type === "collection") overCollectionId = oData.collectionId;
      else if (String(over.id).startsWith("col:")) overCollectionId = String(over.id).slice(4);
      else if (String(over.id).startsWith("col-drop:")) overCollectionId = String(over.id).slice(9);
      if (!overCollectionId) return;
      const newIndex = siblings.indexOf(overCollectionId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const reordered = arrayMove(siblings, oldIndex, newIndex);
      try {
        await reorderMut.mutateAsync({ parent_id: parentId, ordered_ids: reordered });
      } catch (err: any) {
        toast({ title: "Falha ao reordenar", description: err?.message, variant: "destructive" });
      }
    }
  };

  // ---- Criar página em coleção ------------------------------------------
  const handleCreatePageIn = (collectionId: string | null) => {
    setPendingCollectionId(collectionId);
    setNewPageTitle("");
    setPageDialogOpen(true);
  };
  const handleConfirmCreatePage = async () => {
    if (!newPageTitle.trim()) return;
    const id = await createPage.mutateAsync({
      space_id: spaceId,
      title: newPageTitle.trim(),
      slug: toSlug(newPageTitle),
      collection_id: pendingCollectionId,
    });
    setPageDialogOpen(false);
    navigate(`/tenant/kb/${spaceId}/pages/${id}`);
  };

  // ---- Render recursivo ---------------------------------------------------
  const renderCollectionNode = useCallback(
    (col: KbCollectionRow, depth: number): React.ReactNode => {
      const isExpanded = expanded.has(col.id);
      const subCols = collectionsByParent.get(col.id) ?? [];
      const subPages = pagesByCollection.get(col.id) ?? [];
      return (
        <CollectionItem
          key={col.id}
          collection={col}
          spaceId={spaceId}
          depth={depth}
          expanded={isExpanded}
          onToggle={() => toggle(col.id)}
          allCollections={collections}
          activePageId={activePageId}
          onCreatePage={(cid) => handleCreatePageIn(cid)}
        >
          <SortableContext
            items={subCols.map((c) => `col:${c.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {subCols.map((sc) => renderCollectionNode(sc, depth + 1))}
          </SortableContext>
          {subPages.map((p) => (
            <PageItem key={p.id} page={p} spaceId={spaceId} depth={depth + 1} activePageId={activePageId} />
          ))}
        </CollectionItem>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded, collectionsByParent, pagesByCollection, collections, activePageId],
  );

  if (collectionsQuery.isLoading || pagesQuery.isLoading) {
    return (
      <div className="space-y-2 p-2">
        <Skeleton className="h-6" /><Skeleton className="h-6 w-4/5" /><Skeleton className="h-6 w-3/5" />
      </div>
    );
  }

  const rootCollections = collectionsByParent.get(null) ?? [];
  const orphanPages = pagesByCollection.get(null) ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
          Coleções
        </span>
        <div className="flex gap-1">
          <Button
            size="sm" variant="ghost" className="h-7 px-2"
            onClick={() => handleCreatePageIn(null)}
            title="Nova página sem coleção"
          >
            <FilePlus className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost" className="h-7 px-2"
            onClick={() => setRootCreateOpen(true)}
            title="Nova coleção"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-0.5">
          {rootCollections.length === 0 && orphanPages.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
              Nenhuma coleção ainda. Clique em <strong>+</strong> para criar a primeira.
            </div>
          )}

          <SortableContext
            items={rootCollections.map((c) => `col:${c.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {rootCollections.map((c) => renderCollectionNode(c, 0))}
          </SortableContext>

          {orphanPages.length > 0 && (
            <div className="pt-3">
              <div className="px-2 pb-1 text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">
                Sem coleção
              </div>
              <OrphanDropZone>
                {orphanPages.map((p) => (
                  <PageItem key={p.id} page={p} spaceId={spaceId} depth={0} activePageId={activePageId} />
                ))}
              </OrphanDropZone>
            </div>
          )}
        </div>

        <DragOverlay>
          {activeId?.startsWith("page:") && (
            <div className="rounded bg-background border shadow-md px-2 py-1 text-sm flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              {pages.find((p) => p.id === activeId.slice(5))?.title ?? "Página"}
            </div>
          )}
          {activeId?.startsWith("col:") && (
            <div className="rounded bg-background border shadow-md px-2 py-1 text-sm flex items-center gap-1.5">
              <Folder className="h-3.5 w-3.5 text-muted-foreground" />
              {collections.find((c) => c.id === activeId.slice(4))?.name ?? "Coleção"}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <KbCollectionDialog
        open={rootCreateOpen}
        onOpenChange={setRootCreateOpen}
        spaceId={spaceId}
        parentId={null}
      />

      {/* Diálogo simples de nova página (título) — usado a partir do menu da coleção */}
      {pageDialogOpen && (
        <QuickNewPageDialog
          open={pageDialogOpen}
          onOpenChange={setPageDialogOpen}
          value={newPageTitle}
          onChange={setNewPageTitle}
          onConfirm={handleConfirmCreatePage}
          submitting={createPage.isPending}
          contextLabel={
            pendingCollectionId
              ? collections.find((c) => c.id === pendingCollectionId)?.name ?? "Coleção"
              : "Sem coleção"
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog inline "Nova página" (título rápido)
// ---------------------------------------------------------------------------



function QuickNewPageDialog({
  open, onOpenChange, value, onChange, onConfirm, submitting, contextLabel,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  value: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
  submitting: boolean;
  contextLabel: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova página em "{contextLabel}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Título</Label>
          <Input
            value={value}
            autoFocus
            maxLength={200}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) onConfirm(); }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onConfirm} disabled={!value.trim() || submitting}>
            {submitting ? "Criando..." : "Criar e editar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
